import ReactPlayer from "react-player";
import classes from "./MediaComponent.module.css";
import { BsFileEarmarkPdfFill } from "react-icons/bs";
import { AiFillEye, AiFillFileWord } from "react-icons/ai";
import { imageUrl, mediaUrl } from "@/config/apiUrl";
import { FaFileCsv } from "react-icons/fa6";
import {
  CSV_MIME_TYPES,
  PDF_MIME_TYPES,
  IMAGE_MIME_TYPES,
  VIDEO_MIME_TYPES,
  DOC_MIME_TYPES,
} from "@/constant/constants";
const MediaComponent = ({ media, onShowMore, size = "large" }) => {
  const getFileType = (item) => {
    // Check if item has key (server file) or type (local file)
    if (item?.key) {
      if (isIncludedInMimeTypes(item?.name, IMAGE_MIME_TYPES)) return "image";
      if (isIncludedInMimeTypes(item?.name, VIDEO_MIME_TYPES)) return "video";
      if (isIncludedInMimeTypes(item?.name, CSV_MIME_TYPES)) return "csv";
      if (isIncludedInMimeTypes(item?.name, PDF_MIME_TYPES)) return "pdf";
      if (isIncludedInMimeTypes(item?.name, DOC_MIME_TYPES)) return "doc";
    } else {
      // Local file - check by type
      if (item?.type?.split("/")[0] === "image") return "image";
      if (item?.type?.split("/")[0] === "video") return "video";
      if (item?.type === "text/csv") return "csv";
      if (item?.type === "application/pdf") return "pdf";
      if (isIncludedInMimeTypes(item?.name, DOC_MIME_TYPES)) return "doc";
    }
    return null;
  };

  // Separate media into images/videos and documents
  const imagesAndVideos =
    media?.filter(
      (item) => getFileType(item) === "image" || getFileType(item) === "video"
    ) || [];
  const documents =
    media?.filter(
      (item) =>
        getFileType(item) === "pdf" ||
        getFileType(item) === "doc" ||
        getFileType(item) === "csv"
    ) || [];

  const renderImageContent = (item, fileType) => {
    if (fileType === "image") {
      return (
        <img
          src={item?.key ? imageUrl(item?.key) : URL.createObjectURL(item)}
          alt=""
        />
      );
    }
    if (fileType === "video") {
      return (
        <ReactPlayer
          url={item?.key ? mediaUrl(item?.key) : URL.createObjectURL(item)}
          playing={false}
          controls={true}
          width={"100%"}
          height={"100%"}
          className={classes.videoPlayer}
        />
      );
    }
    return null;
  };

  const renderDocumentIcon = (item, fileType) => {
    switch (fileType) {
      case "pdf":
        return <BsFileEarmarkPdfFill size={24} color={`#ff1300`} />;
      case "doc":
        return <AiFillFileWord size={24} color={`#004db3`} />;
      case "csv":
        return <FaFileCsv size={24} color={`#43B257`} />;
      default:
        return null;
    }
  };

  const getImageLayoutClass = (index, totalImages) => {
    if (totalImages === 1) return classes.image_full;
    if (totalImages === 2) return classes.image_half;
    if (totalImages >= 3) {
      if (index === 0) return classes.image_full;
      return classes.image_half;
    }
    return classes.image_full;
  };

  const getImageContainerClass = (totalImages) => {
    if (totalImages === 1) return classes.images_container_single;
    if (totalImages === 2) return classes.images_container_two;
    if (totalImages >= 3) return classes.images_container_three;
    return classes.images_container_single;
  };

  // Get the original index in the media array
  const getOriginalIndex = (item, isImage) => {
    return media?.findIndex((m) => m === item);
  };

  return (
    <div className={classes.media_section}>
      {/* Images and Videos Row */}
      {imagesAndVideos.length > 0 && (
        <div className={getImageContainerClass(imagesAndVideos.length)}>
          {imagesAndVideos.length >= 3 ? (
            <>
              {/* First image - full width */}
              <div
                className={`${classes.image} ${classes.image_full}`}
                key={`img-0`}
                onClick={() =>
                  onShowMore &&
                  onShowMore(getOriginalIndex(imagesAndVideos[0], true))
                }
              >
                {renderImageContent(
                  imagesAndVideos[0],
                  getFileType(imagesAndVideos[0])
                )}
              </div>
              {/* Next two images - side by side */}
              <div className={classes.images_container_three_row}>
                {imagesAndVideos.slice(1, 3).map((item, index) => {
                  const fileType = getFileType(item);
                  const originalIndex = getOriginalIndex(item, true);
                  return (
                    <div
                      className={`${classes.image} ${classes.image_half}`}
                      key={`img-${index + 1}`}
                      onClick={() => onShowMore && onShowMore(originalIndex)}
                    >
                      {renderImageContent(item, fileType)}
                      {imagesAndVideos.length > 3 && index === 1 && (
                        <div className={classes.show_more}>
                          <p>+{imagesAndVideos.length - 3}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            imagesAndVideos.slice(0, 3).map((item, index) => {
              const fileType = getFileType(item);
              const originalIndex = getOriginalIndex(item, true);
              const layoutClass = getImageLayoutClass(
                index,
                imagesAndVideos.length
              );
              return (
                <div
                  className={`${classes.image} ${layoutClass}`}
                  key={`img-${index}`}
                  onClick={() => onShowMore && onShowMore(originalIndex)}
                >
                  {renderImageContent(item, fileType)}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Documents Row */}
      {documents.length > 0 && (
        <div className={classes.documents_row}>
          {documents.slice(0, 2).map((item, index) => {
            const fileType = getFileType(item);
            const originalIndex = getOriginalIndex(item, false);
            return (
              <div
                key={`doc-${index}`}
                className={classes.document_icon}
                onClick={() => {
                  window?.open(
                    item?.key ? mediaUrl(item?.key) : URL.createObjectURL(item),
                    "_blank"
                  );
                }}
              >
                {renderDocumentIcon(item, fileType)} <span>{item?.name}</span>
              </div>
            );
          })}
          {documents.length > 2 && (
            <div
              className={classes.document_plus}
              onClick={() =>
                onShowMore && onShowMore(getOriginalIndex(documents[2], false))
              }
            >
              <p>+{documents.length - 2}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default MediaComponent;

const isIncludedInMimeTypes = (mimeType, mimeTypes) => {
  return mimeTypes?.includes(mimeType?.split(".").pop());
};
